import { Link } from 'react-router-dom';
import { useState, type ReactNode } from 'react';
import { Network, SearchCheck } from 'lucide-react';
import { Card, EmptyState, Flag, PageHeader, Select, StatusPill, TableSkeleton } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { useSeoNetwork } from '@/hooks/useSeo';
import { useSearchNetwork } from '@/hooks/useSearchConsole';
import { useSeoMetricsNetwork } from '@/hooks/useSeoMetrics';
import { formatRelative } from '@/utils/format';
import { label, TIER_LABELS } from '@/utils/labels';
import { DOMAIN_VERDICT, SEO_LEVEL, seoIssueText } from '@/utils/seo';
import type { DomainMetricsView, SearchNetworkLine, SeoNetworkSite } from '@/types/api';

const LEVELS = [
  { value: '', label: 'Все сайты' },
  { value: 'critical', label: 'Критично' },
  { value: 'warning', label: 'Есть замечания' },
  { value: 'ok', label: 'В порядке' },
  { value: 'none', label: 'Не проверялись' },
];

const rank: Record<string, number> = { critical: 0, warning: 1, info: 2, ok: 3, none: 4 };

/**
 * SEO across the network (ADR-0045): every site's standing with search
 * engines at a glance — the worst first — and the groups of market versions
 * hreflang ties together, where a link one way and not back is what breaks.
 */
export function SeoPage(): ReactNode {
  const network = useSeoNetwork();
  // Google's numbers (ADR-0049) and the domains' (ADR-0050), beside each site's own checks.
  const search = new Map((useSearchNetwork().data ?? []).map((line) => [line.siteId, line]));
  const metrics = new Map((useSeoMetricsNetwork().data ?? []).map((line) => [line.siteId, line.view]));
  const [level, setLevel] = useState('');
  const sites = [...(network.data?.sites ?? [])].sort((a, b) => rank[a.level ?? 'none']! - rank[b.level ?? 'none']! || a.name.localeCompare(b.name));
  const shown = level ? sites.filter((site) => (site.level ?? 'none') === level || (level === 'ok' && site.level === 'info')) : sites;
  const count = (value: string) => sites.filter((site) => (site.level ?? 'none') === value).length;
  const byId = new Map(sites.map((site) => [site.siteId, site]));

  return (
    <div className="page">
      <PageHeader title="SEO" subtitle="Индексация, sitemap, hreflang, данные Google и показатели доменов" />

      {network.isPending ? (
        <Card flush>
          <TableSkeleton rows={5} columns={6} />
        </Card>
      ) : network.isError ? (
        <ErrorState error={network.error} />
      ) : (
        <div className="stack">
          <div className="grid-cards">
            {(['critical', 'warning', 'ok', 'none'] as const).map((value) => (
              <button
                key={value}
                type="button"
                className={`card metric-card metric-button${level === value ? ' metric-card-active' : ''}`}
                aria-pressed={level === value}
                onClick={() => setLevel(level === value ? '' : value)}
              >
                <div className="card-body">
                  <p className="metric-label">
                    <StatusPill map={SEO_LEVEL} status={value} />
                  </p>
                  <p className="metric-value">{value === 'ok' ? count('ok') + count('info') : count(value)}</p>
                </div>
              </button>
            ))}
          </div>

          <div className="toolbar">
            <Select label="Состояние" size="sm" value={level} onChange={setLevel} options={LEVELS} />
          </div>

          <Card flush title="Сайты" icon={SearchCheck}>
            {shown.length === 0 ? (
              <EmptyState icon={SearchCheck} message={sites.length === 0 ? 'Нет сайтов с доменом' : 'Нет сайтов по заданным условиям'} />
            ) : (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Сайт</th>
                      <th scope="col">Страна и язык</th>
                      <th scope="col">Индексация</th>
                      <th scope="col">Sitemap</th>
                      <th scope="col">Google, 28 дн.</th>
                      <th scope="col">Домен</th>
                      <th scope="col">Состояние</th>
                      <th scope="col">Замечание</th>
                      <th scope="col">Проверен</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((site) => (
                      <Row key={site.siteId} site={site} search={search.get(site.siteId)} metrics={metrics.get(site.siteId)} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card title="Языковые версии (hreflang)" icon={Network}>
            {(network.data?.groups ?? []).length === 0 ? (
              <p className="muted">Связей hreflang между сайтами нет.</p>
            ) : (
              <ul className="list-plain stack">
                {network.data!.groups.map((group) => (
                  <li key={group.join()} className="row-tight row-wrap">
                    {group.map((id) => {
                      const site = byId.get(id);
                      const broken = site?.issues.some((issue) => issue.code === 'hreflang_not_reciprocal');
                      return (
                        <Link key={id} to={`/sites/${id}?tab=seo`} className={`tag${broken ? ' tag-warning' : ''}`} title={broken ? 'Не все ссылки hreflang возвращаются' : undefined}>
                          {site?.domain ?? id}
                          {site?.lang ? ` · ${site.lang}` : ''}
                        </Link>
                      );
                    })}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

function Row({ site, search, metrics }: { readonly site: SeoNetworkSite; readonly search: SearchNetworkLine | undefined; readonly metrics: DomainMetricsView | undefined }): ReactNode {
  const top = site.issues[0];
  return (
    <tr>
      <td>
        <Link to={`/sites/${site.siteId}?tab=seo`}>{site.name}</Link>
        {site.domain && site.domain !== site.name ? <div className="muted text-sm mono">{site.domain}</div> : null}
      </td>
      <td>
        {site.country || site.language ? (
          <span className="with-flag">
            <Flag country={site.country} language={site.language} />
            {[site.country, site.language].filter(Boolean).join(' · ')}
          </span>
        ) : (
          <span className="faint">—</span>
        )}
        {site.lang && site.language && site.lang.split('-')[0] !== site.language.split('-')[0] ? <div className="text-sm field-error">страница: {site.lang}</div> : null}
      </td>
      <td>
        {site.indexable === null ? (
          <span className="faint">—</span>
        ) : site.indexable ? (
          <span className={site.tier === 'staging' ? 'tone-degraded' : ''} title={site.tier === 'staging' ? 'Тестовый сайт открыт для поисковиков' : undefined}>
            Открыта
          </span>
        ) : (
          <span className={site.tier === 'staging' ? '' : 'tone-down'} title={site.tier === 'staging' ? undefined : 'Рабочий сайт закрыт от поисковиков'}>
            Закрыта
          </span>
        )}
        {site.tier === 'staging' ? <div className="muted text-sm">{label(TIER_LABELS, site.tier)}</div> : null}
      </td>
      <td>{site.sitemapUrls !== null ? site.sitemapUrls.toLocaleString('ru-RU') : <span className="faint">—</span>}</td>
      <td className="nowrap">
        {search?.state === 'verified' && search.clicks !== null ? (
          <>
            {search.clicks.toLocaleString('ru-RU')} кл. · {(search.impressions ?? 0).toLocaleString('ru-RU')} пок.
            {search.checked ? (
              <div className={`text-sm ${search.indexed === search.checked ? 'muted' : 'tone-degraded'}`}>
                в индексе {search.indexed} из {search.checked}
              </div>
            ) : null}
          </>
        ) : (
          <span className="faint">{search ? 'подключается' : '—'}</span>
        )}
      </td>
      <td className="nowrap">
        {metrics?.verdict ? <StatusPill map={DOMAIN_VERDICT} status={metrics.verdict} /> : <span className="faint">—</span>}
        {metrics?.openpagerank?.data?.pageRank !== undefined && metrics.openpagerank.data.pageRank !== null ? (
          <div className="muted text-sm">PR {metrics.openpagerank.data.pageRank.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}</div>
        ) : null}
      </td>
      <td>
        <StatusPill map={SEO_LEVEL} status={site.level ?? 'none'} />
      </td>
      <td className="text-sm">
        {top && top.severity !== 'info' ? seoIssueText(top) : <span className="faint">—</span>}
        {site.issues.filter((issue) => issue.severity !== 'info').length > 1 ? <span className="muted"> · и ещё {site.issues.filter((issue) => issue.severity !== 'info').length - 1}</span> : null}
      </td>
      <td className="nowrap">{site.checkedAt ? formatRelative(site.checkedAt) : <span className="faint">—</span>}</td>
    </tr>
  );
}
